// shell/components/table/columns/image-column.ts

import { ImageColumnType, ImageColumnValue } from '../types';
import { ColumnRenderer, CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class ImageColumnRenderer<T = unknown> extends ColumnRenderer<T> {
    readonly supportsGrid = true;
    readonly supportsTable = false; // Images only in grid mode

    /** Shared ResizeObserver for all responsive images */
    private readonly resizeObserver: ResizeObserver;
    private readonly resizeCallbacks = new Map<HTMLElement, (width: number) => void>();

    constructor(protected config: ImageColumnType<T>) {
        super();

        // Single ResizeObserver for all responsive images from this renderer
        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const callback = this.resizeCallbacks.get(entry.target as HTMLElement);
                callback?.(entry.contentRect.width);
            }
        });
    }

    getGridCellClass(): string {
        return 'cs-column-image';
    }

    getTableCellClass(): string {
        return 'cs-column-image';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement | null {
        const value = ctx.value as ImageColumnValue | null;
        if (!value) return this.buildPlaceholder();

        if (this.config.fullWidth) {
            cellWrapper.classList.add('cs-column-image--full-width');
        }

        return this.buildImage(value, ctx.item, cellWrapper);
    }

    private buildImage(value: ImageColumnValue, item: T, cellWrapper: HTMLElement): HTMLElement {
        const hasImage = value.url || value.blob || (value.urlset?.length ?? 0) > 0;

        const img = document.createElement('img');
        img.className = 'cs-column-image__img';

        // Accessibility
        img.alt = value.alt ?? '';

        // Set dimensions
        if (value.width) img.width = value.width;
        if (value.height) img.height = value.height;
        if (value.width && value.height) {
            img.style.aspectRatio = `${value.width} / ${value.height}`;
        }

        // Constraints
        if (this.config.maxHeight) img.style.maxHeight = `${this.config.maxHeight}px`;
        if (this.config.maxWidth) img.style.maxWidth = `${this.config.maxWidth}px`;

        // Rotation
        if (value.rotation) {
            img.style.transform = `rotate(${value.rotation}deg)`;
            if (value.rotation === 90 || value.rotation === 270) {
                // Adjust for rotated dimensions
                const dim = `100% * ${value.width} / ${value.height}`;
                img.style.width = `calc(${dim})`;
                img.style.marginLeft = `calc((100% - ${dim}) / 2)`;
            }
        }

        // Lazy loading
        if (this.config.lazy) {
            img.loading = 'lazy';
        }

        // Error handling
        img.onerror = () => {
            img.classList.add('cs-column-image__img--error');
            img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        };

        if (!hasImage) {
            img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            img.classList.add('cs-column-image__img--missing');
            return img;
        }

        // Handle srcset for responsive images
        if (value.urlset && value.urlset.length > 0) {
            this.setupResponsiveImage(img, value, cellWrapper);
        } else if (this.config.customFetch) {
            try {
                this.config.customFetch(img, value, item);
            } catch (e) {
                console.error('Custom image fetch error:', e);
                img.classList.add('cs-column-image__img--error');
            }
        } else if (value.url) {
            img.src = value.url;
        } else if (value.blob) {
            this.setupBlobImage(img, value.blob, cellWrapper);
        }

        return img;
    }

    private buildPlaceholder(): HTMLElement {
        const placeholder = document.createElement('div');
        placeholder.className = 'cs-column-image__placeholder';
        return placeholder;
    }

    private setupBlobImage(img: HTMLImageElement, blob: Blob, cellWrapper: HTMLElement): void {
        const blobUrl = URL.createObjectURL(blob);
        img.src = blobUrl;

        // Register cleanup to revoke blob URL
        this.registerCleanup(cellWrapper, () => {
            URL.revokeObjectURL(blobUrl);
        });
    }

    private setupResponsiveImage(
        img: HTMLImageElement,
        value: ImageColumnValue,
        cellWrapper: HTMLElement
    ): void {
        const thumbs = [...value.urlset!].sort((a, b) => a.intrinsicSize - b.intrinsicSize);
        const aspectRatio = value.width / value.height;
        let currentUrl: string | null = null;

        const updateForWidth = (containerWidth: number) => {
            if (containerWidth <= 0) return;

            // Account for device pixel ratio for sharp images on retina displays
            const dpr = window.devicePixelRatio || 1;

            // Calculate the required image size based on container and aspect ratio
            // For landscape (wide) images: width is the constraint
            // For portrait (tall) images: height is the constraint, so need larger source
            let targetSize = containerWidth * dpr;
            if (aspectRatio < 1) {
                // Portrait: container height would be containerWidth / aspectRatio
                // We need an image that covers that height
                targetSize = (containerWidth / aspectRatio) * dpr;
            }

            let match = thumbs.find(t => t.intrinsicSize >= targetSize);
            if (!match) match = thumbs[thumbs.length - 1];

            if (currentUrl !== match.url) {
                currentUrl = match.url;
                img.src = currentUrl;
            }
        };

        // Set initial src to smallest thumbnail to avoid blank state
        // This will be upgraded once container size is known
        img.src = thumbs[0].url;
        currentUrl = thumbs[0].url;

        // Try to update immediately if container is already sized
        const initialWidth = cellWrapper.offsetWidth;
        if (initialWidth > 0) {
            updateForWidth(initialWidth);
        }

        // Observe for resize to upgrade image as container grows
        this.resizeCallbacks.set(cellWrapper, updateForWidth);
        this.resizeObserver.observe(cellWrapper);

        // Register cleanup to stop observing
        this.registerCleanup(cellWrapper, () => {
            this.resizeCallbacks.delete(cellWrapper);
            this.resizeObserver.unobserve(cellWrapper);
        });
    }

    buildTableCell(): HTMLElement | null {
        return null; // Not supported in table mode
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        // For images, always do a full rebuild on update (handles row recycling)
        if (type === CellUpdateType.Full) {
            // Clean up old image resources
            this.cleanupElement(cellWrapper);

            // Remove old content
            if (contentEl) {
                contentEl.remove();
            }

            // Build new image
            const value = ctx.value as ImageColumnValue | null;
            const newContent = value ? this.buildImage(value, ctx.item, cellWrapper) : this.buildPlaceholder();
            cellWrapper.appendChild(newContent);

            return { contentElement: newContent };
        }

        // Shades update - images don't support shading
        return { contentElement: contentEl };
    }

    updateTableCell(ctx: CellRenderContext<T>, td: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        // Not supported
        return { contentElement: null };
    }

    dispose(): void {
        // Disconnect observer and clear all callbacks
        this.resizeObserver.disconnect();
        this.resizeCallbacks.clear();
        super.dispose();
    }
}
